import { createContext } from 'react';
import utils from 'utils';
import { PublicListingRequirementKey } from 'router/subrouters/ListingCreationWizard/data/publicListingRequirements';
import { PublicListingRequirementState } from 'router/subrouters/ListingCreationWizard/hooks/usePublicListingRequirements';

type PreviewMissingFieldsContextProps = {
  /** The shared rule set resolved against the current values. */
  requirements: PublicListingRequirementState[];
  /** Required and not yet satisfied, in the order the fields appear. */
  missingKeys: PublicListingRequirementKey[];
  missingCount: number;
  /**
   * Satisfied by the broker rather than by the deal. Zero means nothing has been
   * entered on the page yet, which the bottom bar reads.
   */
  enteredCount: number;
  isRequired: (key: PublicListingRequirementKey) => boolean;
  isSatisfied: (key: PublicListingRequirementKey) => boolean;
  /**
   * Requirements the broker has been shown as outstanding. An empty draft marks
   * nothing - a field earns its mark by being opened and left unsatisfied, or by
   * "Show missing fields" catching it.
   */
  isMarked: (key: PublicListingRequirementKey) => boolean;
  /** The "opened it and left it" trigger. No-op unless the rule is unmet. */
  markIfMissing: (key: PublicListingRequirementKey) => void;
  /** "Show missing fields" - marks every outstanding requirement at once. */
  markAllMissing: () => void;
  /**
   * The field to scroll to and open, and optionally the control inside it to
   * land on - the financial block opens as a panel of several. Cleared by the
   * field once it has taken focus.
   */
  focusRequest: PreviewFocusRequest | null;
  requestFocus: (fieldName: string, focusFieldName?: string) => void;
  consumeFocusRequest: () => void;
};

export type PreviewFocusRequest = {
  fieldName: string;
  focusFieldName?: string;
};

export default createContext<Partial<PreviewMissingFieldsContextProps>>({
  requirements: [],
  missingKeys: [],
  missingCount: 0,
  enteredCount: 0,
  isRequired: () => false,
  isSatisfied: () => false,
  isMarked: () => false,
  markIfMissing: utils.noop,
  markAllMissing: utils.noop,
  focusRequest: null,
  requestFocus: utils.noop,
  consumeFocusRequest: utils.noop,
});
